import { h, ref } from 'vue';

import createVueApp from '@phoenix/createVueApp';
import { lazySelector } from '@sg-core/resources/assets/js/utils';

import PhxsgTabs from './phxsg-tabs.vue';

import type {
  PhxsgTabsCssEnv,
  PhxsgTabsLayout,
  PhxsgTabsOrientation,
  PhxsgTabsPanelData,
  PhxsgTabsResponsiveLayout,
  PhxsgTabsTransitionMode,
} from './tabs.types';

function getElementChildNodes(element: HTMLElement | null) {
  return element ? Array.from(element.childNodes) : [];
}

function getPanelAttributes(panel: HTMLElement) {
  return Object.fromEntries(
    Array.from(panel.attributes)
      .filter(({ name }) => {
        return (
          name !== 'id' &&
          name !== 'class' &&
          name !== 'role' &&
          name !== 'tabindex' &&
          name !== 'aria-labelledby' &&
          name !== 'hidden' &&
          name !== 'data-tabs-panel' &&
          !name.startsWith('data-tab-')
        );
      })
      .map(({ name, value }) => [name, value]),
  );
}

const VALID_ORIENTATIONS = new Set<PhxsgTabsOrientation>(['horizontal', 'vertical']);
const VALID_LAYOUTS = new Set<PhxsgTabsLayout>(['stretch', 'start', 'expandable']);
const VALID_TRANSITION_MODES = new Set<PhxsgTabsTransitionMode>(['default', 'in-out', 'out-in']);

function parseBoolean(value: string | undefined) {
  return value === 'true';
}

function parseNumber(value: string | undefined, fallback: number) {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function parseOrientation(value: string | undefined): PhxsgTabsOrientation {
  return VALID_ORIENTATIONS.has(value as PhxsgTabsOrientation) ? (value as PhxsgTabsOrientation) : 'horizontal';
}

function parseLayout(value: string | undefined): PhxsgTabsLayout {
  return VALID_LAYOUTS.has(value as PhxsgTabsLayout) ? (value as PhxsgTabsLayout) : 'stretch';
}

function parseTransitionMode(value: string | undefined): PhxsgTabsTransitionMode {
  return VALID_TRANSITION_MODES.has(value as PhxsgTabsTransitionMode) ? (value as PhxsgTabsTransitionMode) : 'default';
}

function parseResponsiveLayouts(value: string | undefined): PhxsgTabsResponsiveLayout[] {
  if (!value) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue.filter(
      (item): item is PhxsgTabsResponsiveLayout =>
        typeof item === 'object' &&
        item !== null &&
        'layout' in item &&
        VALID_LAYOUTS.has(String(item.layout) as PhxsgTabsLayout),
    );
  } catch {
    return [];
  }
}

function parseTabButtonClasses(value: string | undefined): string[] {
  if (!value) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue.filter((item): item is string => typeof item === 'string' && item !== '');
  } catch {
    return [];
  }
}

function parseCssEnv(value: string | undefined): PhxsgTabsCssEnv {
  if (!value) {
    return {};
  }

  try {
    const parsedValue = JSON.parse(value);

    if (typeof parsedValue !== 'object' || parsedValue === null || Array.isArray(parsedValue)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsedValue).filter(
        ([key, entryValue]) => key.startsWith('--breakpoint-') && typeof entryValue === 'string',
      ),
    ) as PhxsgTabsCssEnv;
  } catch {
    return {};
  }
}

function parsePanels(element: HTMLElement): PhxsgTabsPanelData[] {
  const panelsContainer = Array.from(element.children).find((child): child is HTMLElement => {
    return child instanceof HTMLElement && child.hasAttribute('data-tabs-panels');
  });

  if (!panelsContainer) {
    return [];
  }

  return Array.from(panelsContainer.children)
    .filter((panel): panel is HTMLElement => panel instanceof HTMLElement && panel.hasAttribute('data-tabs-panel'))
    .map((panel) => {
      const id = panel.dataset.tabId;
      const label = panel.dataset.tabLabel;

      if (!id || !label) {
        return null;
      }

      return {
        id,
        label,
        shortLabel: panel.dataset.tabShortLabel || '',
        icon: panel.dataset.tabIcon || '',
        iconPath: panel.dataset.tabIconPath || '',
        iconSpritePath: panel.dataset.tabIconSpritePath || '',
        badge: panel.dataset.tabBadge || '',
        empty: panel.dataset.tabEmpty === 'true',
        disabled: panel.dataset.tabDisabled === 'true',
        className: panel.className,
        attributes: getPanelAttributes(panel),
        nodes: getElementChildNodes(panel),
      };
    })
    .filter((panel): panel is PhxsgTabsPanelData => panel !== null);
}

function dispatchChangeEvent(element: HTMLElement, id: string, button: EventTarget | null) {
  element.dispatchEvent(
    new CustomEvent('change', {
      bubbles: true,
      detail: {
        id,
        button,
      },
    }),
  );
}

export class Tabs {
  private element: HTMLElement;

  private valueObserver: MutationObserver | null = null;

  private app: ReturnType<typeof createVueApp> | null = null;

  constructor(element: HTMLElement | string) {
    this.element = lazySelector(element);

    if (this.element.dataset.tabsMounted === 'true') {
      return;
    }

    this.init();
  }

  destroy() {
    if (this.valueObserver) {
      this.valueObserver.disconnect();
      this.valueObserver = null;
    }

    if (this.app) {
      this.app.unmount();
      this.app = null;
    }

    this.element.dataset.tabsMounted = 'false';
  }

  private init() {
    const startSlotElement = this.element.querySelector<HTMLElement>('[data-tabs-slot="start"]');
    const endSlotElement = this.element.querySelector<HTMLElement>('[data-tabs-slot="end"]');
    const panels = parsePanels(this.element);
    const initialValue = this.element.dataset.value ?? null;

    this.app = createVueApp({
      setup: () => {
        const currentValue = ref(initialValue);

        this.valueObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-value') {
              currentValue.value = this.element.dataset.value ?? null;
            }
          }
        });

        this.valueObserver.observe(this.element, {
          attributes: true,
          attributeFilter: ['data-value'],
        });

        return () =>
          h(PhxsgTabs, {
            rootElement: this.element,
            panels,
            value: currentValue.value,
            teleportTarget: this.element.dataset.teleportTarget || null,
            cssEnv: parseCssEnv(this.element.dataset.cssEnv),
            orientation: parseOrientation(this.element.dataset.orientation),
            layout: parseLayout(this.element.dataset.layout),
            tabButtonClasses: parseTabButtonClasses(this.element.dataset.tabButtonClasses),
            responsiveLayouts: parseResponsiveLayouts(this.element.dataset.responsiveLayouts),
            tabButtonsIcon: this.element.dataset.tabButtonsIcon || '',
            showIndicator:
              this.element.dataset.showIndicator !== undefined
                ? parseBoolean(this.element.dataset.showIndicator)
                : true,
            transitionName: this.element.dataset.transitionName || 'phxsg-tabs__panel-',
            transitionMode: parseTransitionMode(this.element.dataset.transitionMode),
            contentOffset: parseNumber(this.element.dataset.contentOffset, 40),
            indicatorThickness: parseNumber(this.element.dataset.indicatorThickness, 2),
            startSlotNodes: getElementChildNodes(startSlotElement),
            endSlotNodes: getElementChildNodes(endSlotElement),
            onChange: (id: string, button: EventTarget | null) => {
              this.element.dataset.value = id;
              currentValue.value = id;
              dispatchChangeEvent(this.element, id, button);
            },
          });
      },
    });

    this.app.mount(this.element);
    this.element.dataset.tabsMounted = 'true';
  }
}
